import React, { useState, useEffect } from 'react';
import { ref, onValue, remove, get } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import { database, auth } from '../firebase';
import PurchaseInvoice from './PurchaseInvoice';
import PayableForm from './PayableForm';
import ReactDOMServer from 'react-dom/server'; // <-- Add this line

const PurchaseBillTable = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [showTransactionForm, setShowTransactionForm] = useState(false);
  const [transactionToEdit, setTransactionToEdit] = useState(null);
  const [showBillDetails, setShowBillDetails] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [branchProfile, setBranchProfile] = useState(null);

  const encodeEmailForPath = (email) => {
    return email.replace(/[.#$[\]]/g, '_');
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
    });
    return () => unsubscribe();
  }, []);

  // Fetch branch profile data
  useEffect(() => {
    if (!currentUser) return;

    const encodedEmail = encodeEmailForPath(currentUser.email);
    const branchProfilesRef = ref(database, `users/${encodedEmail}/branchProfiles`);

    const unsubscribe = onValue(
      branchProfilesRef,
      (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const profiles = Object.entries(data);
          const latestProfile = profiles[profiles.length - 1];
          setBranchProfile(latestProfile[1]);
        } else {
          setBranchProfile(null);
        }
      },
      (error) => {
        console.error('Error fetching branch profile:', error);
      }
    );

    return () => unsubscribe();
  }, [currentUser]);

useEffect(() => {
  if (!currentUser) return;

  const encodedEmail = encodeEmailForPath(currentUser.email);
  const invoicesRef = ref(database, `users/${encodedEmail}/invoices`);

  const unsubscribe = onValue(
    invoicesRef,
    (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const invoiceList = Object.keys(data).map((key) => {
          const invoice = {
            id: key,
            ...data[key],
            supplierDetails: {
              ...data[key].supplierDetails,
              paymentStatus: data[key].supplierDetails?.paymentStatus || 'Pending',
            },
            summary: data[key].summary || {
              netAmt: 0,
              outstanding: 0,
              totalPaid: 0,
            },
            items: data[key].items || [],
          };

          // Ensure numerical values with proper parsing
          const netAmt = Number(invoice.summary.netAmt) || 0;
          const totalPaid = Number(invoice.summary.totalPaid) || 0;
          
          // Use the stored outstanding value if it exists and is valid, otherwise calculate
          const storedOutstanding = Number(invoice.summary.outstanding);
          const calculatedOutstanding = Math.max(0, netAmt - totalPaid);
          const finalOutstanding = !isNaN(storedOutstanding) && storedOutstanding >= 0 
            ? storedOutstanding 
            : calculatedOutstanding;

          // Update the invoice object with corrected values
          invoice.summary.netAmt = netAmt;
          invoice.summary.totalPaid = totalPaid;
          invoice.summary.outstanding = finalOutstanding;

          // Synchronize paymentStatus with outstanding amount
          invoice.supplierDetails.paymentStatus = finalOutstanding <= 0.01 ? 'Complete' : 'Pending';
          invoice.summary.paymentStatus = finalOutstanding <= 0.01 ? 'Complete' : 'Pending';

          return invoice;
        });
        setInvoices(invoiceList);
      } else {
        setInvoices([]);
      }
    },
    (error) => {
      console.error('Error fetching invoices:', error);
    }
  );

  return () => unsubscribe();
}, [currentUser]);

  const handleAddPurchaseBill = () => {
    setEditingInvoice(null);
    setShowInvoiceForm(true);
  };

  const handleEditInvoice = (invoice) => {
    setEditingInvoice(invoice);
    setShowInvoiceForm(true);
  };

const handleViewDetailsNewTab = async (invoice) => {
  if (!invoice || !invoice.supplierDetails) {
    alert('Invoice details are not available.');
    return;
  }

  let supplierMobileNo = 'N/A';
  
  // Fetch supplier mobile number from suppliers collection
  if (currentUser && invoice.supplierDetails.supplier) {
    try {
      const encodedEmail = encodeEmailForPath(currentUser.email);
      const suppliersRef = ref(database, `users/${encodedEmail}/suppliers`);
      const snapshot = await get(suppliersRef);
      
      if (snapshot.exists()) {
        const suppliers = snapshot.val();
        const supplier = Object.values(suppliers).find(
          s => s.supplierName === invoice.supplierDetails.supplier
        );
        if (supplier) {
          supplierMobileNo = supplier.mobileNo || supplier.phoneNo || 'N/A';
        }
      }
    } catch (error) {
      console.error('Error fetching supplier mobile:', error);
    }
  }

  const printContent = (
    <div style={{
      backgroundColor: 'white',
      padding: '30px',
      margin: '20px',
      border: '2px solid #3b82f6',
      borderRadius: '12px',
      fontFamily: 'Arial, sans-serif',
      fontSize: '12px',
      pageBreakInside: 'avoid',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
    }}>
      {/* Company Header */}
      <div style={{
        textAlign: 'center',
        marginBottom: '30px',
        paddingBottom: '20px',
        borderBottom: '2px solid #e2e8f0'
      }}>
        <h1 style={{
          fontSize: '24px',
          fontWeight: 'bold',
          margin: '0',
          marginBottom: '5px',
          color: '#1e293b',
          textShadow: '1px 1px 2px rgba(0,0,0,0.1)'
        }}>
          {branchProfile?.branchName || 'SUPREME TRADERS'}
        </h1>
        <div style={{ fontSize: '16px', fontWeight: '600', color: '#3b82f6', marginBottom: '10px' }}>
          {branchProfile?.city || (branchProfile?.address1 ? branchProfile.address1.split(/\s+/).pop() : 'Unknown City')}
        </div>
        <div style={{ fontSize: '14px', color: '#475569' }}>
          Purchase Invoice
        </div>
      </div>

      {/* Invoice Info */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '2rem',
        marginBottom: '2rem',
        padding: '1rem',
        backgroundColor: '#f8fafc',
        borderRadius: '6px',
        border: '1px solid #e2e8f0'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <div style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#374151' }}>
            📋 Invoice No: {invoice.supplierDetails.invoiceNo || 'N/A'}
          </div>
          <div style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#374151' }}>🏪 Supplier: {invoice.supplierDetails.supplier || 'N/A'}</div>
          
          <div style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#374151' }}>📍 Address:  {invoice.supplierDetails.address || 'N/A'}<br />
            {invoice.supplierDetails.cityPincode || ''}</div>
          
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <div style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#374151' }}>
            📅 Date: {invoice.supplierDetails.invoiceDate || 'N/A'}
          </div>
          <div style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#374151' }}>
            📊 MRN No: {invoice.supplierDetails.mrnNo || 'N/A'}
          </div>
          <div style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#374151' }}>
            🎯 Terms: {invoice.supplierDetails.terms || 'N/A'}
          </div>
          <div style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#374151' }}>
            📱 Mobile No: {supplierMobileNo}
          </div>
        </div>
      </div>

      {/* Items Table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '11px',
          border: '2px solid #3b82f6',
          borderRadius: '8px',
          overflow: 'hidden',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <thead style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #3b82f6' }}>
            <tr>
              <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>ITEMS</th>
              <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>QTY</th>
              <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>RATE</th>
              <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>AMOUNT</th>
            </tr>
          </thead>
          <tbody>
            {invoice.items.map((item, index) => (
              <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                <td style={{ border: '1px solid #cbd5e1', padding: '8px', fontSize: '11px', textAlign: 'left' }}>
                  {index + 1}. {item.item || 'N/A'}
                  {item.batch && <div style={{ fontSize: '9px', color: '#666' }}>Batch: {item.batch}</div>}
                </td>
                <td style={{ border: '1px solid #cbd5e1', padding: '8px', fontSize: '11px', textAlign: 'center' }}>{item.qty || 0}</td>
                <td style={{ border: '1px solid #cbd5e1', padding: '8px', fontSize: '11px', textAlign: 'center' }}>₹{Number(item.pRate || 0).toFixed(2)}</td>
                <td style={{ border: '1px solid #cbd5e1', padding: '8px', fontSize: '11px', textAlign: 'center' }}>₹{Number(item.netValue || 0).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Totals Section */}
      <div style={{
        marginTop: '30px',
        padding: '20px',
        backgroundColor: '#f8fafc',
        borderRadius: '8px',
        border: '1px solid #e2e8f0',
        width: '300px',
        marginLeft: 'auto'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', fontSize: '11px' }}>
          <span>Gross Amount:</span>
          <span>₹{Number(invoice.summary.grossAmt || 0).toFixed(2)}</span>
        </div>
        {Number(invoice.summary.cgstAmt || 0) > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', fontSize: '11px' }}>
            <span>CGST:</span>
            <span>₹{Number(invoice.summary.cgstAmt || 0).toFixed(2)}</span>
          </div>
        )}
        {Number(invoice.summary.sgstAmt || 0) > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', fontSize: '11px' }}>
            <span>SGST:</span>
            <span>₹{Number(invoice.summary.sgstAmt || 0).toFixed(2)}</span>
          </div>
        )}
        {Number(invoice.summary.igstAmt || 0) > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', fontSize: '11px' }}>
            <span>IGST:</span>
            <span>₹{Number(invoice.summary.igstAmt || 0).toFixed(2)}</span>
          </div>
        )}
        {Number(invoice.summary.cessAmt || 0) > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', fontSize: '11px' }}>
            <span>CESS:</span>
            <span>₹{Number(invoice.summary.cessAmt || 0).toFixed(2)}</span>
          </div>
        )}
        {Number(invoice.summary.discount || 0) > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', fontSize: '11px' }}>
            <span>Discount:</span>
            <span>-₹{Number(invoice.summary.discount || 0).toFixed(2)}</span>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px', background: '#f0f0f0', fontWeight: 'bold', fontSize: '12px', borderTop: '2px solid #000', border: '1px solid #000' }}>
          <span>NET TOTAL:</span>
          <span>₹{Number(invoice.summary.netAmt || 0).toFixed(2)}</span>
        </div>
      </div>

      {/* Thank You Message */}
      <div style={{
        textAlign: 'center',
        marginTop: '30px',
        padding: '10px',
        background: '#f0f9ff',
        border: '1px solid #000',
        fontWeight: 'bold',
        fontSize: '12px'
      }}>
        *** Thank You Visit Again ***
      </div>
    </div>
  );

  const htmlString = ReactDOMServer.renderToString(printContent);

  const newWindow = window.open('', '_blank');
  if (newWindow) {
    newWindow.document.write(`
      <html>
        <head>
          <title>Purchase Invoice - ${invoice.supplierDetails.invoiceNo || 'N/A'}</title>
          <style>
            body { margin: 0; padding: 0; font-family: Arial, sans-serif; }
            @page { size: A4; margin: 5mm; }
            table { page-break-inside: auto; }
            tr { page-break-inside: avoid; page-break-after: auto; }
          </style>
        </head>
        <body>
          ${htmlString}
          <script>
            setTimeout(function() {
              window.print();
            }, 500);
          </script>
        </body>
      </html>
    `);
    newWindow.document.close();
  } else {
    alert('Unable to open new tab for printing.');
  }
};

  const handleTransactionForm = (invoice) => {
    const netAmt = Number(invoice.summary?.netAmt || 0);
    const totalPaid = Number(invoice.summary?.totalPaid || 0);
    const currentOutstanding = Math.max(0, netAmt - totalPaid);

    // Fetch the next voucher number
    const encodedEmail = encodeEmailForPath(currentUser.email);
    const transactionsRef = ref(database, `users/${encodedEmail}/transactions`);
    get(transactionsRef)
      .then((snapshot) => {
        let maxVoucherNo = 0;
        if (snapshot.exists()) {
          const transactions = snapshot.val();
          const voucherNos = Object.values(transactions)
            .map((transaction) => parseInt(transaction.voucherNo, 10))
            .filter((num) => !isNaN(num));
          maxVoucherNo = voucherNos.length > 0 ? Math.max(...voucherNos) : 0;
        }

        setTransactionToEdit({
          id: null,
          supplier: invoice.supplierDetails.supplier || '',
          supplierAddress: invoice.supplierDetails.address || '',
          supplierCity: invoice.supplierDetails.cityPincode || '',
          totalAmount: netAmt.toFixed(2),
          netAmount: currentOutstanding.toFixed(2),
          paymentAmount: '0.00',
          voucherNo: (maxVoucherNo + 1).toString(),
          voucherDate: new Date().toISOString().split('T')[0],
          transactions: invoice.items.map((item, index) => ({
            sno: index + 1,
            transactionType: 'Purchase',
            remarks: '',
            mrnNo: invoice.supplierDetails.mrnNo || '',
            mrnDt: '',
            invNo: invoice.supplierDetails.invoiceNo || '',
            invDt: invoice.supplierDetails.invoiceDate || '',
            item: item.item || '',
            batch: item.batch || '',
            qty: item.qty || 0,
            unitPrice: item.unitPrice ? Number(item.unitPrice).toFixed(2) : '0.00',
            netValue: item.netValue ? Number(item.netValue).toFixed(2) : '0.00',
            paidAmt: '0.00',
            adjAmt: '0.00',
            balance: item.netValue ? Number(item.netValue).toFixed(2) : '0.00',
            paymentAmt: '',
            invoiceId: invoice.id,
            itemId: item.id || '',
          })),
        });
        setShowTransactionForm(true);
      })
      .catch((error) => {
        console.error('Error fetching Voucher No:', error);
        setTransactionToEdit({
          id: null,
          supplier: invoice.supplierDetails.supplier || '',
          supplierAddress: invoice.supplierDetails.address || '',
          supplierCity: invoice.supplierDetails.cityPincode || '',
          totalAmount: netAmt.toFixed(2),
          netAmount: currentOutstanding.toFixed(2),
          paymentAmount: '0.00',
          voucherNo: '1',
          voucherDate: new Date().toISOString().split('T')[0],
          transactions: invoice.items.map((item, index) => ({
            sno: index + 1,
            transactionType: 'Purchase',
            remarks: '',
            mrnNo: invoice.supplierDetails.mrnNo || '',
            mrnDt: '',
            invNo: invoice.supplierDetails.invoiceNo || '',
            invDt: invoice.supplierDetails.invoiceDate || '',
            item: item.item || '',
            batch: item.batch || '',
            qty: item.qty || 0,
            unitPrice: item.unitPrice ? Number(item.unitPrice).toFixed(2) : '0.00',
            netValue: item.netValue ? Number(item.netValue).toFixed(2) : '0.00',
            paidAmt: '0.00',
            adjAmt: '0.00',
            balance: item.netValue ? Number(item.netValue).toFixed(2) : '0.00',
            paymentAmt: '',
            invoiceId: invoice.id,
            itemId: item.id || '',
          })),
        });
        setShowTransactionForm(true);
      });
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!currentUser) {
      alert('Please login to delete invoice');
      return;
    }

    if (window.confirm('Are you sure you want to delete this invoice?')) {
      try {
        const encodedEmail = encodeEmailForPath(currentUser.email);
        const invoiceRef = ref(database, `users/${encodedEmail}/invoices/${invoiceId}`);
        await remove(invoiceRef);
        alert('Invoice deleted successfully!');
      } catch (error) {
        console.error('Error deleting invoice:', error);
        alert('Failed to delete invoice.');
      }
    }
  };

  const handleTransactionSuccess = () => {
    setShowTransactionForm(false);
    setTransactionToEdit(null);
  };

  const handleBackToTable = () => {
    setShowInvoiceForm(false);
    setShowTransactionForm(false);
    setShowBillDetails(false);
    setEditingInvoice(null);
    setTransactionToEdit(null);
    setSelectedInvoice(null);
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'Complete':
        return '#10b981';
      case 'Partial':
        return '#f59e0b';
      default:
        return '#ef4444';
    }
  };

  const getPaymentStatusText = (status) => {
    switch (status) {
      case 'Complete':
        return '✅ Complete';
      case 'Partial':
        return '🔄 Partial';
      default:
        return '⏳ Pending';
    }
  };

  const printBill = () => {
    window.print();
  };

  const styles = {
    container: {
      maxWidth: '100%',
      margin: '0 auto',
      padding: '2rem',
      minHeight: '100vh',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem',
      padding: '1.5rem',
      backgroundColor: 'white',
      borderRadius: '6px',
      border: '1px solid #d1d5db',
      fontWeight: '600',
      color: '#374151',
    },
    title: {
      fontSize: '2rem',
      fontWeight: '700',
      color: '#2563eb',
      margin: '0',
    },
    addButton: {
      padding: '0.875rem 2rem',
      borderRadius: '8px',
      backgroundColor: '#2563eb',
      color: 'white',
      fontWeight: '600',
      cursor: 'pointer',
      border: 'none',
      fontSize: '0.95rem',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 15px rgba(37, 99, 235, 0.3)',
      transform: 'translateY(0)',
    },
    tableContainer: {
      backgroundColor: 'white',
      borderRadius: '16px',
      overflow: 'hidden',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e2e8f0',
      overflowX: 'auto',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '0.9rem',
      border: '1px solid #d1d5db',
    },
    tableHeader: {
      padding: '1rem 0.75rem',
      textAlign: 'center',
      backgroundColor: '#2563eb',
      color: 'white',
      fontWeight: '600',
      fontSize: '0.85rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      border: '1px solid #1e40af',
    },
    tableCell: {
      padding: '1rem 0.75rem',
      border: '1px solid #d1d5db',
      color: '#374151',
      verticalAlign: 'middle',
      textAlign: 'center',
    },
    noData: {
      textAlign: 'center',
      padding: '4rem 2rem',
      color: '#718096',
      fontSize: '1.1rem',
    },
    srNoCell: {
      fontWeight: '600',
      textAlign: 'center',
      width: '60px',
    },
    actionCell: {
      display: 'flex',
      gap: '0.5rem',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '0.5rem',
    },
    editButton: {
      padding: '0.5rem',
      borderRadius: '6px',
      backgroundColor: '#10b981',
      color: 'white',
      fontWeight: '500',
      cursor: 'pointer',
      border: 'none',
      fontSize: '0.8rem',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
      transform: 'translateY(0)',
    },
    deleteButton: {
      padding: '0.5rem',
      borderRadius: '6px',
      backgroundColor: '#ef4444',
      color: 'white',
      fontWeight: '500',
      cursor: 'pointer',
      border: 'none',
      fontSize: '0.8rem',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(220, 38, 38, 0.3)',
      transform: 'translateY(0)',
    },
    paymentStatusBadge: {
      padding: '0.25rem 0.75rem',
      borderRadius: '20px',
      fontSize: '0.75rem',
      fontWeight: '600',
      color: 'white',
      textAlign: 'center',
      display: 'inline-block',
      minWidth: '80px',
    },
    // Bill Details Styles
    billContainer: {
      maxWidth: '800px',
      margin: '0 auto',
      backgroundColor: 'white',
      padding: '2rem',
      borderRadius: '8px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      fontFamily: 'Arial, sans-serif',
    },
    billHeader: {
      textAlign: 'center',
      marginBottom: '2rem',
      borderBottom: '2px solid #2563eb',
      paddingBottom: '1rem',
    },
    companyName: {
      fontSize: '1.5rem',
      fontWeight: 'bold',
      color: '#2563eb',
      marginBottom: '0.5rem',
    },
    companyDetails: {
      fontSize: '0.9rem',
      color: '#666',
      lineHeight: '1.4',
    },
    billInfo: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '2rem',
      marginBottom: '2rem',
      padding: '1rem',
      backgroundColor: '#f8fafc',
      borderRadius: '6px',
    },
    infoSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
    },
    infoLabel: {
      fontSize: '0.8rem',
      fontWeight: 'bold',
      color: '#374151',
    },
    infoValue: {
      fontSize: '0.9rem',
      color: '#111827',
    },
    itemsTable: {
      width: '100%',
      borderCollapse: 'collapse',
      marginBottom: '2rem',
      border: '1px solid #d1d5db',
    },
    itemsHeader: {
      backgroundColor: '#2563eb',
      color: 'white',
      fontWeight: 'bold',
      fontSize: '0.9rem',
    },
    itemsHeaderCell: {
      padding: '0.75rem',
      textAlign: 'center',
      border: '1px solid #1e40af',
    },
    itemsCell: {
      padding: '0.75rem',
      textAlign: 'center',
      border: '1px solid #d1d5db',
      fontSize: '0.85rem',
    },
    itemsRow: {
      backgroundColor: '#fff',
    },
    totalsSection: {
      marginTop: '2rem',
      marginLeft: 'auto',
      width: '300px',
    },
    totalRow: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '0.5rem',
      borderBottom: '1px solid #e5e7eb',
    },
    netTotalRow: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '0.75rem',
      backgroundColor: '#f3f4f6',
      fontWeight: 'bold',
      fontSize: '1.1rem',
      borderTop: '2px solid #2563eb',
    },
    thankYouMessage: {
      textAlign: 'center',
      marginTop: '2rem',
      padding: '1rem',
      backgroundColor: '#f0f9ff',
      borderRadius: '6px',
      color: '#1e40af',
      fontWeight: 'bold',
    },
    backButton: {
      padding: '0.5rem 1rem',
      borderRadius: '6px',
      backgroundColor: '#6b7280',
      color: 'white',
      fontWeight: '500',
      cursor: 'pointer',
      border: 'none',
      fontSize: '0.8rem',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(107, 114, 128, 0.3)',
      transform: 'translateY(0)',
      marginBottom: '1rem',
    },
    printButton: {
      padding: '0.5rem 1rem',
      borderRadius: '6px',
      backgroundColor: '#059669',
      color: 'white',
      fontWeight: '500',
      cursor: 'pointer',
      border: 'none',
      fontSize: '0.8rem',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(5, 150, 105, 0.3)',
      transform: 'translateY(0)',
      marginLeft: '0.5rem',
      marginBottom: '1rem',
    },
  };

  if (!currentUser) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', padding: '4rem', color: '#718096', fontSize: '1.1rem' }}>
          <h2>Please login to access the Purchase Bill Entries</h2>
        </div>
      </div>
    );
  }

  if (showInvoiceForm) {
    return <PurchaseInvoice onBack={handleBackToTable} editingInvoice={editingInvoice} />;
  }

  if (showTransactionForm) {
    return (
      <PayableForm
        transactionToEdit={transactionToEdit}
        onSuccess={handleTransactionSuccess}
        onCancel={handleBackToTable}
      />
    );
  }

  if (showBillDetails && selectedInvoice) {
    const companyName = branchProfile?.branchName || 'SUPREME TRADERS';
    const address = branchProfile?.address1 || '';
    const city = branchProfile?.city || '';
    const gstNo = branchProfile?.gstinNo || 'N/A';
    const phoneNo = branchProfile?.phoneNo || branchProfile?.mobileNo || '7508894659';

    return (
      <div style={styles.container}>
        <div>
          <button
            onClick={handleBackToTable}
            style={styles.backButton}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = '0 4px 12px rgba(107, 114, 128, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 2px 8px rgba(107, 114, 128, 0.3)';
            }}
          >
            ← Back to List
          </button>
          <button
            onClick={printBill}
            style={styles.printButton}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = '0 4px 12px rgba(5, 150, 105, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 2px 8px rgba(5, 150, 105, 0.3)';
            }}
          >
            🖨️ Print
          </button>
          <button
            onClick={() => handleViewDetailsNewTab(selectedInvoice)}
            style={styles.printButton}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = '0 4px 12px rgba(5, 150, 105, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 2px 8px rgba(5, 150, 105, 0.3)';
            }}
          >
            🔗 Open in New Tab
          </button>
        </div>

        <div style={styles.billContainer}>
          {/* Company Header */}
          <div style={styles.billHeader}>
            <div style={styles.companyName}>{companyName}</div>
            <div style={styles.companyDetails}>
              Purchase Invoice<br />
              {address}{city ? ', ' + city : ''}<br />
              GST No: {gstNo}<br />
              Ph: {phoneNo} / 33-GSTIN : 27ABIPG4508D1ZN
            </div>
          </div>

          {/* Invoice Info */}
          <div style={styles.billInfo}>
            <div style={styles.infoSection}>
              <div style={styles.infoLabel}>📋 Invoice No: {selectedInvoice.supplierDetails.invoiceNo || 'N/A'}</div>
              <div style={styles.infoLabel}>🏪 Supplier:</div>
              <div style={styles.infoValue}>{selectedInvoice.supplierDetails.supplier || 'N/A'}</div>
              <div style={styles.infoLabel}>📍 Address:</div>
              <div style={styles.infoValue}>
                {selectedInvoice.supplierDetails.address || 'N/A'}<br />
                {selectedInvoice.supplierDetails.cityPincode || ''}
              </div>
            </div>
            <div style={styles.infoSection}>
              <div style={styles.infoLabel}>📅 Date: {selectedInvoice.supplierDetails.invoiceDate || 'N/A'}</div>
              <div style={styles.infoLabel}>📊 MRN No: {selectedInvoice.supplierDetails.mrnNo || 'N/A'}</div>
              <div style={styles.infoLabel}>🎯 Terms: {selectedInvoice.supplierDetails.terms || 'N/A'}</div>
              <div style={styles.infoLabel}>📧 Mobile No:</div>
<div style={styles.infoValue}>{selectedInvoice.supplierDetails.mobileNo || selectedInvoice.supplierDetails.phoneNo || 'N/A'}</div>
            </div>
          </div>

          {/* Items Table */}
          <table style={styles.itemsTable}>
            <thead style={styles.itemsHeader}>
              <tr>
                <th style={styles.itemsHeaderCell}>ITEMS</th>
                <th style={styles.itemsHeaderCell}>QTY</th>
                <th style={styles.itemsHeaderCell}>RATE</th>
                <th style={styles.itemsHeaderCell}>AMOUNT</th>
              </tr>
            </thead>
            <tbody>
              {selectedInvoice.items.map((item, index) => (
                <tr key={index} style={styles.itemsRow}>
                  <td style={{ ...styles.itemsCell, textAlign: 'left' }}>
                    {index + 1}. {item.item || 'N/A'}
                    {item.batch && <div style={{ fontSize: '0.75rem', color: '#666' }}>Batch: {item.batch}</div>}
                    
                  </td>
                  <td style={styles.itemsCell}>{item.qty || 0}</td>
                  <td style={styles.itemsCell}>₹{Number(item.pRate || 0).toFixed(2)}</td>
                  <td style={styles.itemsCell}>₹{Number(item.netValue || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totals Section */}
          <div style={styles.totalsSection}>
            <div style={styles.totalRow}>
              <span>Gross Amount:</span>
              <span>₹{Number(selectedInvoice.summary.grossAmt || 0).toFixed(2)}</span>
            </div>
            {selectedInvoice.summary.cgstAmt > 0 && (
              <div style={styles.totalRow}>
                <span>CGST:</span>
                <span>₹{Number(selectedInvoice.summary.cgstAmt || 0).toFixed(2)}</span>
              </div>
            )}
            {selectedInvoice.summary.sgstAmt > 0 && (
              <div style={styles.totalRow}>
                <span>SGST:</span>
                <span>₹{Number(selectedInvoice.summary.sgstAmt || 0).toFixed(2)}</span>
              </div>
            )}
            {selectedInvoice.summary.igstAmt > 0 && (
              <div style={styles.totalRow}>
                <span>IGST:</span>
                <span>₹{Number(selectedInvoice.summary.igstAmt || 0).toFixed(2)}</span>
              </div>
            )}
            {selectedInvoice.summary.cessAmt > 0 && (
              <div style={styles.totalRow}>
                <span>CESS:</span>
                <span>₹{Number(selectedInvoice.summary.cessAmt || 0).toFixed(2)}</span>
              </div>
            )}
            {selectedInvoice.summary.discount > 0 && (
              <div style={styles.totalRow}>
                <span>Discount:</span>
                <span>-₹{Number(selectedInvoice.summary.discount || 0).toFixed(2)}</span>
              </div>
            )}
            <div style={styles.netTotalRow}>
              <span>NET TOTAL:</span>
              <span>₹{Number(selectedInvoice.summary.netAmt || 0).toFixed(2)}</span>
            </div>
          </div>

          {/* Thank You Message */}
          <div style={styles.thankYouMessage}>
            *** Thank You Visit Again ***
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Purchase Bill Entries</h1>
        <button
          onClick={handleAddPurchaseBill}
          style={styles.addButton}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 6px 20px rgba(37, 99, 235, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 4px 15px rgba(37, 99, 235, 0.3)';
          }}
        >
          + Add Purchase Bill Entry
        </button>
      </div>

      <div style={styles.tableContainer}>
        {invoices.length > 0 ? (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.tableHeader, ...styles.srNoCell }}>MRN NO</th>
                <th style={styles.tableHeader}>MRN DATE</th>
                <th style={styles.tableHeader}>SUPPLIER</th>
                <th style={styles.tableHeader}>INVOICE NO</th>
                <th style={styles.tableHeader}>INVOICE DATE</th>
                <th style={styles.tableHeader}>NET AMOUNT</th>
                <th style={styles.tableHeader}>TOTAL PAID</th>
                <th style={styles.tableHeader}>OUTSTANDING</th>
                <th style={styles.tableHeader}>PAYMENT STATUS</th>
                <th style={styles.tableHeader}>ACTION</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice, index) => (
                <tr
                  key={invoice.id}
                  style={{
                    backgroundColor: index % 2 === 0 ? '#f7fafc' : 'white',
                    transition: 'background-color 0.2s ease',
                  }}
                >
                  <td style={{ ...styles.tableCell, ...styles.srNoCell }}>
                    {invoice.supplierDetails.mrnNo || 'N/A'}
                  </td>
                  <td style={styles.tableCell}>
                    {invoice.supplierDetails.invoiceDate || 'N/A'}
                  </td>
                  <td style={styles.tableCell}>
                    {invoice.supplierDetails.supplier || 'N/A'}
                  </td>
                  <td style={styles.tableCell}>
                    {invoice.supplierDetails.invoiceNo || 'N/A'}
                  </td>
                  <td style={styles.tableCell}>
                    {invoice.supplierDetails.invoiceDate || 'N/A'}
                  </td>
                  <td style={styles.tableCell}>
                    <strong>₹{Number(invoice.summary?.netAmt || 0).toFixed(2)}</strong>
                  </td>
                  <td style={styles.tableCell}>
                    ₹{Number(invoice.summary?.totalPaid || 0).toFixed(2)}
                  </td>
                  <td style={styles.tableCell}>
                    <strong style={{ 
                      color: Number(invoice.summary?.outstanding || 0) <= 0.01 ? '#10b981' : '#ef4444' 
                    }}>
                      ₹{Number(invoice.summary?.outstanding || 0).toFixed(2)}
                    </strong>
                  </td>
                  <td style={styles.tableCell}>
                    <span 
                      style={{
                        ...styles.paymentStatusBadge,
                        backgroundColor: getPaymentStatusColor(invoice.summary?.paymentStatus || 'Pending')
                      }}
                    >
                      {getPaymentStatusText(invoice.summary?.paymentStatus || 'Pending')}
                    </span>
                  </td>
                  <td style={{ ...styles.tableCell, ...styles.actionCell }}>
                    <button
                      onClick={() => handleEditInvoice(invoice)}
                      style={styles.editButton}
                      title="Edit Invoice"
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.3)';
                      }}
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleViewDetailsNewTab(invoice)}
                      style={styles.editButton}
                      title="View Details (New Tab)"
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.3)';
                      }}
                    >
                      📝
                    </button>
                    <button
                      onClick={() => handleTransactionForm(invoice)}
                      style={styles.editButton}
                      title="Make Payment"
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.3)';
                      }}
                    >
                      💸
                    </button>
                    <button
                      onClick={() => handleDeleteInvoice(invoice.id)}
                      style={styles.deleteButton}
                      title="Delete Invoice"
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 2px 8px rgba(220, 38, 38, 0.3)';
                      }}
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div style={styles.noData}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem', opacity: '0.5' }}>📄</div>
            <h3 style={{ color: '#4a5568', marginBottom: '0.5rem' }}>No Purchase Bills Found</h3>
            <p style={{ marginBottom: '1.5rem' }}>Get started by adding your first purchase bill!</p>
            <button
              onClick={handleAddPurchaseBill}
              style={{
                ...styles.addButton,
                fontSize: '1rem',
                padding: '1rem 2rem',
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(37, 99, 235, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 15px rgba(37, 99, 235, 0.3)';
              }}
            >
              + Add Your First Purchase Bill
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PurchaseBillTable;